import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";

import Heading from "../../../shared/ui/Typography/Heading";
import Text from "../../../shared/ui/Typography/Text";
import Spinner from "../../../shared/ui/Loading/Spinner";

import { fetchClientViewings } from "../api/viewings";
import { messageFromError } from "../../../shared/api/drfErrors";

import ClientCreateViewingModal from "./ClientCreateViewingModal";

// ClientViewingsSection — subordinate hydration boundary inside
// /clients/:clientId (CRM-WORKFLOW-C1) + T-3 cross-trigger modal owner.
//
// Owns:
//   * GET /crm/viewings/?client=<id>
//   * viewing list, loading/error/empty/refresh state
//   * create-modal lifecycle and post-create section-local refetch
//   * primary "Запланувати перегляд" button (when canEdit)
//
// Does NOT own client core, route validity, activity/tasks data,
// global Redux, shared cache, or cross-section coordination.

const STATUS_LABELS = {
  scheduled: "Заплановано",
  completed: "Завершено",
  cancelled: "Скасовано",
};

const formatDateTimeUk = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("uk-UA", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const agentDisplayName = (agent) => {
  if (!agent) return "—";
  if (typeof agent.fullname === "string" && agent.fullname.trim()) {
    return agent.fullname;
  }
  if (typeof agent.handle === "string" && agent.handle.trim()) {
    return agent.handle;
  }
  return "—";
};

const unwrapListPayload = (data) => {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.results)) return data.results;
  return [];
};

function ClientViewingsSection({ clientId, canEdit = true }, ref) {
  const [viewings, setViewings] = useState([]);
  const [loadState, setLoadState] = useState("loading");
  const [loadError, setLoadError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);

  useImperativeHandle(
    ref,
    () => ({
      openViewingModal() {
        setCreateOpen(true);
      },
    }),
    [],
  );

  useEffect(() => {
    if (clientId == null) return undefined;

    let cancelled = false;
    setLoadState("loading");
    setLoadError(null);

    fetchClientViewings(clientId)
      .then((data) => {
        if (cancelled) return;
        setViewings(unwrapListPayload(data));
        setLoadState("loaded");
      })
      .catch((err) => {
        if (cancelled) return;
        setLoadError(messageFromError(err));
        setLoadState("error");
      });

    return () => {
      cancelled = true;
    };
  }, [clientId]);

  const handleReload = async () => {
    if (refreshing) return;
    setRefreshing(true);
    setRefreshError(null);
    try {
      const data = await fetchClientViewings(clientId);
      setViewings(unwrapListPayload(data));
      setLoadState("loaded");
    } catch (err) {
      setRefreshError(messageFromError(err));
    } finally {
      setRefreshing(false);
    }
  };

  const list = viewings;

  return (
    <section
      className="cd-section feature-client-detail__section--viewings"
      aria-labelledby="client-detail-viewings-heading"
    >
      <header className="cd-section__head">
        <Heading
          id="client-detail-viewings-heading"
          level={2}
          variant="section"
          icon="fa-solid fa-calendar-check"
        >
          Перегляди
        </Heading>
        <div className="feature-client-detail__section-actions">
          {canEdit ? (
            <button
              type="button"
              className="cd-add-btn"
              onClick={() => setCreateOpen(true)}
            >
              + Запланувати перегляд
            </button>
          ) : null}
          <button
            type="button"
            className="feature-client-detail__refresh-icon-btn"
            onClick={handleReload}
            disabled={refreshing || loadState === "loading"}
            data-loading={refreshing ? "true" : undefined}
            aria-label="Оновити перегляди"
            title="Оновити"
          >
            {refreshing ? (
              <Spinner size="sm" label="Оновлення" />
            ) : (
              <i
                className="fa-solid fa-arrows-rotate"
                aria-hidden="true"
              />
            )}
          </button>
        </div>
      </header>

      {refreshError ? (
        <div className="feature-client-detail__refresh-error" role="alert">
          {refreshError}
        </div>
      ) : null}

      {loadState === "loading" ? (
        <div className="feature-client-detail__section-state">
          <Spinner size="md" label="Завантаження переглядів" />
        </div>
      ) : loadState === "error" ? (
        <div className="feature-client-detail__section-state">
          <Text as="p" size="sm" tone="danger">
            {loadError || "Не вдалося завантажити перегляди."}
          </Text>
        </div>
      ) : list.length === 0 ? (
        <Text as="p" size="sm" tone="secondary">
          Запланованих переглядів по клієнту немає.
        </Text>
      ) : (
        <ul className="feature-client-detail__list">
          {list.map((viewing) => {
            const statusKey = viewing.status || "scheduled";
            const statusLabel = STATUS_LABELS[statusKey] || statusKey || "—";
            const propertyId = viewing.property;
            const propertyTitle =
              viewing.property_title || `Об'єкт #${propertyId || "—"}`;
            return (
              <li
                key={viewing.id}
                className="feature-client-detail__list-item feature-client-detail__list-item--viewing"
              >
                <div className="feature-client-detail__viewing-body">
                  <div className="feature-client-detail__viewing-head">
                    {propertyId ? (
                      <Link
                        to={`/realty/${propertyId}`}
                        className="feature-client-detail__viewing-property"
                      >
                        {propertyTitle}
                      </Link>
                    ) : (
                      <span className="feature-client-detail__viewing-property">
                        {propertyTitle}
                      </span>
                    )}
                    <span
                      className={`feature-client-detail__viewing-status feature-client-detail__viewing-status--${statusKey}`}
                    >
                      {statusLabel}
                    </span>
                  </div>
                  <div className="feature-client-detail__viewing-meta">
                    {viewing.scheduled_at ? (
                      <span className="feature-client-detail__viewing-datetime">
                        <i className="fa-solid fa-clock" aria-hidden="true" />
                        <time dateTime={viewing.scheduled_at}>
                          {formatDateTimeUk(viewing.scheduled_at)}
                        </time>
                      </span>
                    ) : null}
                    <span className="feature-client-detail__viewing-agent">
                      <i className="fa-solid fa-user" aria-hidden="true" />
                      <span>{agentDisplayName(viewing.agent)}</span>
                    </span>
                  </div>
                  {viewing.notes ? (
                    <p className="feature-client-detail__viewing-notes">
                      {viewing.notes}
                    </p>
                  ) : null}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {canEdit ? (
        <ClientCreateViewingModal
          open={createOpen}
          onClose={() => setCreateOpen(false)}
          clientId={clientId}
          onSaved={() => {
            setCreateOpen(false);
            toast.success("Перегляд заплановано.");
            handleReload();
          }}
        />
      ) : null}
    </section>
  );
}

export default forwardRef(ClientViewingsSection);
